import * as DocConvertPlainText from "../../../helpers/Doc/Convert/PlainText";
import { isInlineNode } from "../../../helpers/Doc/Functions/Inline";

const labelToDescription: Record<string, string> = {
  "author": "the author",
  "associate editor": "the associate editor",
  "patient name": "the patient",
};

const ARIA_SNIPPET_MAX = 90;

export interface AriaLabelContextOptions {
  title?: any;
  type?: string;
  uid?: string;
}

function squishWhitespace(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

function truncateSnippet(s: string, max = ARIA_SNIPPET_MAX): string {
  const t = squishWhitespace(s);
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function shortUidSuffix(uid?: string): string | null {
  if (!uid || typeof uid !== "string") {
    return null;
  }
  const compact = uid.replace(/-/g, "");
  if (compact.length < 8) {
    return compact || null;
  }
  return compact.slice(-8);
}

function extractLabelFromContent(content?: any): string | null {
  if (!content) {
    return null;
  }

  if (typeof content === "string") {
    const match = content.match(/<(?:b|strong)>([^<]+)<\/(?:b|strong)>/i);
    if (match) {
      return match[1].toLowerCase().replace(/:$/, "").trim();
    }
  }

  if (content?.text) {
    const colonIndex = content.text.indexOf(":");
    if (colonIndex > 0 && colonIndex < 30) {
      return content.text.substring(0, colonIndex).toLowerCase().trim();
    }
  }

  return null;
}

function isListItemArray(c: any): c is Array<{ type?: string; content?: any }> {
  return (
    Array.isArray(c) &&
    c.length > 0 &&
    c.every((x) => x && x.type === "listItem")
  );
}

function plainTextFromListItem(item: { content?: any }): string {
  const c = item?.content;
  if (!c) {
    return "";
  }
  if (isInlineNode(c)) {
    return DocConvertPlainText.fromAnyType(c);
  }
  if (Array.isArray(c)) {
    return c
      .map((child) => {
        if (!child) {
          return "";
        }
        if (child.type === "paragraph" && child.content && isInlineNode(child.content)) {
          return DocConvertPlainText.fromAnyType(child.content);
        }
        if (child.type === "listItem") {
          return plainTextFromListItem(child);
        }
        return "";
      })
      .filter(Boolean)
      .join(" ");
  }
  return "";
}

function describeListContent(listItems: Array<{ type?: string; content?: any }>): string | null {
  const n = listItems.length;
  const firstText = squishWhitespace(plainTextFromListItem(listItems[0]));
  if (firstText) {
    const snippet = truncateSnippet(firstText);
    if (n === 1) {
      return `List: ${snippet}`;
    }
    return `List, ${n} items, first: ${snippet}`;
  }
  return n > 0 ? `List with ${n} items` : null;
}

function plainTextFromInlineOrHtml(content: any): string | null {
  if (content == null) {
    return null;
  }
  if (Array.isArray(content)) {
    return null;
  }
  const raw = DocConvertPlainText.fromAnyType(content);
  const t = squishWhitespace(raw);
  return t.length > 0 ? t : null;
}

function typeFallbackPhrase(type?: string): string | null {
  switch (type) {
    case "image":
      return "Image";
    case "imageGallery":
      return "Image gallery";
    case "video":
      return "Video";
    case "pdf":
      return "PDF document";
    case "audio":
      return "Audio";
    case "numberedList":
    case "regularList":
      return "List";
    case "paragraph":
      return "Paragraph";
    default:
      return null;
  }
}

function buildContextDescriptor(
  content: any,
  options?: AriaLabelContextOptions
): string {
  const { title, type, uid } = options || {};
  const mappedKey = extractLabelFromContent(content);
  if (mappedKey && labelToDescription[mappedKey]) {
    return labelToDescription[mappedKey];
  }

  if (!isListItemArray(content)) {
    const fromContent = plainTextFromInlineOrHtml(content);
    if (fromContent) {
      return truncateSnippet(fromContent);
    }
  } else {
    const listDesc = describeListContent(content);
    if (listDesc) {
      return listDesc;
    }
  }

  const fromTitle = plainTextFromInlineOrHtml(title);
  if (fromTitle) {
    return truncateSnippet(fromTitle);
  }

  const typePhrase = typeFallbackPhrase(type);
  const idSuffix = shortUidSuffix(uid);
  if (typePhrase && idSuffix) {
    return `${typePhrase} (${idSuffix})`;
  }
  if (typePhrase) {
    return typePhrase;
  }
  if (idSuffix) {
    return `Block ${idSuffix}`;
  }
  return "this section";
}

export function getAriaLabel(
  content: any,
  isActive: boolean,
  activeAction: string,
  inactiveAction: string,
  options?: AriaLabelContextOptions
): string {
  const action = isActive ? activeAction : inactiveAction;
  const context = buildContextDescriptor(content, options);
  return `${action} ${context}`;
}
